import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { debugLog, debugWarn, debugError } from '../utils/debug.js';

/**
 * QR 상태 업데이트를 위한 SSE Context
 * - Server-Sent Events로 실시간 QR 사용 현황 수신
 * - 기존 서비스에 영향 없음 (SSE 실패 시 폴링으로 대체)
 */

const QRStatusContext = createContext(null);

export function QRStatusProvider({ children }) {
  const [qrUpdates, setQrUpdates] = useState({});
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  // 모바일 기기 감지
  const isMobileDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
  };

  // SSE 연결 시도 (비차단, 실패해도 앱 정상 작동)
  const connectSSE = () => {
    try {
      // 이미 연결되어 있으면 중복 연결 방지
      if (eventSourceRef.current?.readyState === EventSource.OPEN) {
        debugLog('[SSE] Already connected');
        return;
      }

      debugLog('[SSE] Connecting to QR status stream...');

      // SSE 엔드포인트 연결
      const eventSource = new EventSource('/api/qr/status-stream');
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        debugLog('[SSE] ✅ Connected to QR status stream');
        setIsConnected(true);
        // 재연결 타이머 클리어
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          debugLog('[SSE] 📨 QR update received:', data);

          // QR 업데이트 정보 저장
          if (data.qr_code_id) {
            setQrUpdates(prev => ({
              ...prev,
              [data.qr_code_id]: {
                ...data,
                timestamp: Date.now()
              }
            }));
          }
        } catch (err) {
          debugWarn('[SSE] Failed to parse message:', err);
        }
      };

      eventSource.onerror = (error) => {
        debugWarn('[SSE] ⚠️ Connection error:', error);
        setIsConnected(false);

        // 연결 종료
        eventSource.close();
        eventSourceRef.current = null;

        // 5초 후 재연결 시도 (무한 루프 방지)
        if (!reconnectTimeoutRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            debugLog('[SSE] Attempting to reconnect...');
            reconnectTimeoutRef.current = null;
            connectSSE();
          }, 5000);
        }
      };

    } catch (err) {
      debugError('[SSE] ❌ Failed to initialize:', err);
      // SSE 실패해도 앱은 정상 작동 (폴링으로 대체)
    }
  };

  // SSE 연결 해제
  const disconnectSSE = () => {
    if (eventSourceRef.current) {
      debugLog('[SSE] Disconnecting...');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsConnected(false);
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  };

  // 컴포넌트 마운트 시 SSE 연결 (선택적)
  useEffect(() => {
    // 모바일 기기에서는 SSE 비활성화 (연결 오류로 인한 성능 문제 방지)
    if (isMobileDevice()) {
      debugLog('[SSE] 📱 Mobile device detected - SSE disabled, using polling only');
      return;
    }

    // SSE 지원 브라우저만 연결
    if (typeof EventSource !== 'undefined') {
      connectSSE();
    } else {
      debugWarn('[SSE] EventSource not supported, falling back to polling');
    }

    return () => {
      disconnectSSE();
    };
  }, []);

  // 특정 QR 코드의 최신 상태 가져오기
  const getQRStatus = (qrCodeId) => {
    return qrUpdates[qrCodeId] || null;
  };

  // QR 상태 구독 (컴포넌트에서 사용)
  const subscribeToQR = (qrCodeId, callback) => {
    debugLog('[SSE] Subscribing to QR:', qrCodeId);

    const checkUpdate = () => {
      const status = qrUpdates[qrCodeId];
      if (status) {
        callback(status);
      }
    };

    // 초기 상태 체크
    checkUpdate();

    // 상태 변경 감지를 위한 interval (SSE 보조)
    const intervalId = setInterval(checkUpdate, 1000);

    // 구독 해제 함수 반환
    return () => {
      clearInterval(intervalId);
    };
  };

  const value = {
    isConnected,
    qrUpdates,
    getQRStatus,
    subscribeToQR,
    reconnect: connectSSE
  };

  return (
    <QRStatusContext.Provider value={value}>
      {children}
    </QRStatusContext.Provider>
  );
}

export function useQRStatus() {
  const context = useContext(QRStatusContext);
  if (!context) {
    // Context가 없어도 에러 발생하지 않음 (기존 서비스 보호)
    debugWarn('[SSE] QRStatusContext not found, SSE features disabled');
    return {
      isConnected: false,
      qrUpdates: {},
      getQRStatus: () => null,
      subscribeToQR: () => () => {},
      reconnect: () => {}
    };
  }
  return context;
}
